import { Badge, Grid, Stack, Text } from '@mantine/core';
import { useState, useMemo } from 'react';
import { formatDate } from '@/services/date';
import { getDosage, getMedicationName, getProvenance } from '@/services/medication';
import { useTimelineContext } from '../hooks/useTimelineContext';
import { MedicationRequestFragment } from '../graphql';
import { ListInput } from './shared/ListInput';
import { DetailRow } from './shared/DetailRow';
import { ListStateMessage } from './shared/ListStateMessage';
import { ORBIT_GRAY } from '@/theme/colors';

interface MedicationItem {
  id: string;
  value?: string;
  medication: MedicationRequestFragment;
}

const STATUS_CONFIG = {
  active: { label: 'ACTIVE', color: 'blue' as const },
  completed: { label: 'COMPLETED', color: 'green' as const },
  stopped: { label: 'STOPPED', color: 'red' as const },
  cancelled: { label: 'CANCELLED', color: 'red' as const },
  'on-hold': { label: 'ON HOLD', color: 'yellow' as const },
  draft: { label: 'DRAFT', color: 'gray' as const },
  'entered-in-error': { label: 'ERROR', color: 'red' as const },
} as const;

function MedicationStatusBadge({ status }: { status?: string }) {
  if (!status) return null;

  const config = STATUS_CONFIG[status.toLowerCase() as keyof typeof STATUS_CONFIG];

  // If status is unknown, show it with a default gray color
  const label = config?.label || status.toUpperCase();
  const color = config?.color || 'gray';

  return (
    <Badge size="xs" color={color} variant="light" style={{ textTransform: 'uppercase', fontWeight: 600 }}>
      {label}
    </Badge>
  );
}

function MedicationItemDisplay({ item }: { item: MedicationItem }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const medicationName = getMedicationName(item.medication);
  const dosage = getDosage(item.medication);
  const provenance = getProvenance(item.medication);
  const date = item.medication.authoredOn ? formatDate(item.medication.authoredOn, 'M/D/YYYY') : 'N/A';

  return (
    <Stack gap={8} style={{ width: '100%', cursor: 'pointer' }} onClick={() => setIsExpanded(!isExpanded)}>
      <Grid align="center" style={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
        <Grid.Col span={6} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <Text
            truncate="end"
            style={{ fontSize: 14, lineHeight: '20px', fontWeight: 400, color: 'var(--mantine-color-black)' }}
          >
            {medicationName}
          </Text>
          <MedicationStatusBadge status={item.medication.status} />
        </Grid.Col>
        <Grid.Col span={6} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          {dosage && (
            <>
              <Text style={{ fontSize: 13, lineHeight: '16px', color: ORBIT_GRAY }}>Dosage</Text>
              <Text
                style={{
                  fontSize: 14,
                  lineHeight: '20px',
                  fontWeight: 400,
                  color: 'var(--mantine-color-black)',
                  textAlign: 'right',
                }}
              >
                {dosage}
              </Text>
            </>
          )}
        </Grid.Col>
      </Grid>

      {isExpanded && (
        <Stack gap={8} style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--mantine-color-gray-3)' }}>
          <DetailRow label="Provenance" value={provenance} />
          <DetailRow label="Date" value={date} />
        </Stack>
      )}
    </Stack>
  );
}

export function MedicationsList() {
  const { patientContext, timelineContext } = useTimelineContext();
  const patient = patientContext?.patient;

  // MedicationsList is only used in patient timeline context, so we can safely access these fields
  const medications = useMemo(() => {
    return 'prescriptionOrdersData' in timelineContext ? timelineContext.prescriptionOrdersData : [];
  }, [timelineContext]);

  const loading = 'prescriptionOrdersLoading' in timelineContext ? timelineContext.prescriptionOrdersLoading : false;
  const error = 'prescriptionOrdersError' in timelineContext ? timelineContext.prescriptionOrdersError : undefined;

  const medicationItems: MedicationItem[] = useMemo(() => {
    if (!medications) return [];
    return medications.map((medication: MedicationRequestFragment) => ({
      id: medication.id || '',
      value: getMedicationName(medication),
      medication,
    }));
  }, [medications]);

  if (!patient || loading || error || medicationItems.length === 0) {
    return (
      <ListStateMessage
        patient={patient}
        loading={loading}
        error={error}
        empty={medicationItems.length === 0}
        emptyMessage="No medications recorded"
        errorMessagePrefix="Error loading medications"
      />
    );
  }

  return (
    <ListInput
      items={medicationItems}
      renderItem={(item) => <MedicationItemDisplay item={item} />}
      addForm={<div />}
      popoverOpened={false}
      setPopoverOpened={() => {}}
    />
  );
}
